import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleStop, Play, Plus, Trash2 } from "lucide-react";
import {
  api,
  ApiError,
  type Announcement,
  type AnnouncementState,
  type AvailableMessage,
  type ConflictBody,
  type Page,
  type PreviewResult,
  type User,
} from "@/lib/api";
import { AnnouncementReview } from "@/features/explore";
import { dateTime, endLabel } from "@/lib/format";
import {
  AudioPlayer,
  Badge,
  Button,
  Card,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Select,
  SortableTh,
  Spinner,
  Table,
  TableFooter,
  Td,
  Th,
  type Tone,
} from "@/components/ui";
import { useServerTable, type TableState } from "@/lib/table";
import { useScope } from "@/app/auth";

/** The words and colours every page uses for an announcement state. */
export const stateInfo: Record<AnnouncementState, { label: string; tone: Tone }> = {
  planned: { label: "geplant", tone: "info" },
  activating: { label: "wird eingespielt", tone: "warning" },
  active: { label: "aktiv", tone: "success" },
  deactivating: { label: "wird beendet", tone: "warning" },
  done: { label: "beendet", tone: "neutral" },
  cancelled: { label: "abgebrochen", tone: "neutral" },
  failed: { label: "fehlgeschlagen", tone: "danger" },
};

export function AnnouncementsPage() {
  const scope = useScope();
  const [creating, setCreating] = useState(false);
  const [stateFilter, setStateFilter] = useState<string>("");

  // Absences accumulate: every one ever planned stays in the list. The server
  // therefore pages and orders them.
  const [total, setTotal] = useState(0);
  const table = useServerTable({
    total,
    initial: { key: "startsAt", dir: "desc" },
    resetOn: [scope, stateFilter],
  });

  const query = useQuery({
    queryKey: ["announcements", scope, stateFilter, table.query],
    queryFn: () => {
      const params = new URLSearchParams(table.query);
      if (stateFilter) params.set("state", stateFilter);
      return api<Page<Announcement>>(`/api/admin/announcements?${params}`);
    },
  });

  useEffect(() => {
    if (query.data) setTotal(query.data.total);
  }, [query.data]);

  const users = useQuery({
    queryKey: ["users", scope, "forAnnouncements"],
    queryFn: () =>
      api<Page<User>>(
        `/api/admin/users?limit=1000&activeOnly=true` +
          (scope ? `&companyId=${scope}` : ""),
      ),
  });

  const byId = useMemo(() => {
    const m = new Map<number, User>();
    for (const u of users.data?.items ?? []) m.set(u.id, u);
    return m;
  }, [users.data]);

  return (
    <>
      <PageHeader
        title="Ansagen"
        description="Geplante und laufende Abwesenheiten. Eine Ansage wird zum Startzeitpunkt automatisch eingespielt und am Ende wieder entfernt."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Ansage planen
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          className="w-48"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
        >
          <option value="">alle Zustände</option>
          {Object.entries(stateInfo).map(([value, { label }]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </div>

      <Card>
        {query.isLoading ? (
          <Spinner />
        ) : query.error ? (
          <div className="p-4">
            <ErrorNote>{String(query.error)}</ErrorNote>
          </div>
        ) : !query.data?.items.length ? (
          <Empty
            title="Keine Ansagen"
            hint="Sobald eine Abwesenheit geplant ist, erscheint sie hier."
          />
        ) : (
          <AnnouncementTable items={query.data.items} users={byId} table={table} />
        )}
        <TableFooter table={table} noun="Ansagen" />
      </Card>

      <CreateDialog
        open={creating}
        onClose={() => setCreating(false)}
        users={users.data?.items ?? []}
      />
    </>
  );
}

function AnnouncementTable({
  items,
  users,
  table,
}: {
  items: Announcement[];
  users: Map<number, User>;
  table: TableState;
}) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["announcements"] });
  // Two verbs, kept apart on purpose. Ending touches the mailbox: the worker
  // puts the standard greeting back, and the row stays until it has. Removing
  // touches only the records, so it is refused while the mailbox still has
  // the announcement.
  const end = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/announcements/${id}/end`, { method: "POST" }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/announcements/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const failure = end.error
    ? describe(end.error, "Die Ansage konnte nicht beendet werden.")
    : remove.error
      ? describe(remove.error, "Die Ansage konnte nicht gelöscht werden.")
      : null;

  return (
    <>
      {failure && (
        <div className="p-4 pb-0">
          <ErrorNote>{failure}</ErrorNote>
        </div>
      )}
      <Table>
        <thead>
          <tr>
            <SortableTh table={table} sortKey="user">
              Benutzer
            </SortableTh>
            <SortableTh table={table} sortKey="startsAt" first="desc">
              Von
            </SortableTh>
            <SortableTh table={table} sortKey="endsAt" first="desc">
              Bis
            </SortableTh>
            <SortableTh table={table} sortKey="state">
              Zustand
            </SortableTh>
            {/* The stand-in is a name the server does not order by; it lives in
                another row and sorting on it would mean a join no listing needs. */}
            <Th>Vertretung</Th>
            <Th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {items.map((a) => {
            const user = users.get(a.userId);
            const proxy = a.proxyUserId ? users.get(a.proxyUserId) : undefined;
            const info = stateInfo[a.state];
            return (
              <tr key={a.id}>
                <Td>
                  <div className="font-medium">
                    {user ? `${user.firstName} ${user.lastName}` : `#${a.userId}`}
                  </div>
                  <div className="text-xs muted">{user?.email}</div>
                </Td>
                <Td>{dateTime(a.startsAt)}</Td>
                <Td>{endLabel(a.endsAt)}</Td>
                <Td>
                  <Badge tone={info.tone}>{info.label}</Badge>
                  {a.errorStage > 0 && (
                    <span className="ml-2 text-xs muted">
                      {a.errorStage === 1 ? "1. Fehlversuch" : "endgültig"}
                    </span>
                  )}
                </Td>
                <Td>{proxy ? `${proxy.firstName} ${proxy.lastName}` : "—"}</Td>
                <Td className="whitespace-nowrap">
                  {a.state === "active" && (
                    <AnnouncementReview id={a.id} />
                  )}
                  {a.state === "active" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Ansage jetzt beenden"
                      title="Ansage jetzt beenden"
                      disabled={end.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            "Diese Ansage jetzt beenden? Die Standardansage wird wieder eingespielt.",
                          )
                        )
                          end.mutate(a.id);
                      }}
                    >
                      <CircleStop className="size-4" aria-hidden />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Ansage löschen"
                    title={onMailbox(a.state) ? "Zuerst beenden" : "Ansage löschen"}
                    disabled={remove.isPending || onMailbox(a.state)}
                    onClick={() => {
                      if (confirm(removalQuestion[a.state])) remove.mutate(a.id);
                    }}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

/** True while the mailbox has the announcement, or the worker is busy with it. */
function onMailbox(state: AnnouncementState) {
  return state === "activating" || state === "active" || state === "deactivating";
}

/**
 * Deleting a planned announcement withdraws it; deleting a finished one only
 * tidies the list. The question says which, because the first is a decision
 * and the second is housekeeping.
 */
const removalQuestion: Record<AnnouncementState, string> = {
  planned: "Diese geplante Ansage wirklich löschen? Sie wird nicht eingespielt.",
  failed: "Diese fehlgeschlagene Ansage wirklich löschen? Sie wird nicht erneut versucht.",
  activating: "",
  active: "",
  deactivating: "",
  done: "Diese beendete Ansage aus der Liste entfernen?",
  cancelled: "Diese abgebrochene Ansage aus der Liste entfernen?",
};

function describe(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.detail : fallback;
}

/**
 * Planning an absence for somebody else is what replaces the old "sign in as
 * this user" feature, which swapped the administrator's own session.
 */
function CreateDialog({
  open,
  onClose,
  users,
}: {
  open: boolean;
  onClose: () => void;
  users: User[];
}) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState<number>(0);
  const [messageId, setMessageId] = useState<number>(0);
  const [proxyId, setProxyId] = useState<number>(0);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [notify, setNotify] = useState("none");
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Announcement[]>([]);

  // Listening to the announcement before it goes on somebody's mailbox is the
  // one check nothing else can make: the blocks resolve, the dates are spoken
  // the way they were meant, and no sentence is missing.
  const preview = useMutation({
    mutationFn: () =>
      api<PreviewResult>("/api/admin/announcements/preview", {
        method: "POST",
        body: {
          userId,
          companyMessageId: messageId,
          proxyUserId: proxyId || undefined,
          endsAt: endsAt ? new Date(endsAt).toISOString() : null,
        },
      }),
  });

  const employees = users.filter((u) => u.role === "employee");

  const messages = useQuery({
    queryKey: ["available-messages", userId],
    queryFn: () =>
      api<AvailableMessage[]>(`/api/admin/users/${userId}/available-messages`),
    enabled: userId > 0,
  });

  const selected = messages.data?.find((m) => m.id === messageId);
  const endRequired = selected?.dateMode !== "end_optional";

  const create = useMutation({
    mutationFn: () =>
      api<Announcement>("/api/admin/announcements", {
        method: "POST",
        body: {
          userId,
          companyMessageId: messageId,
          proxyUserId: proxyId || undefined,
          startsAt: new Date(startsAt).toISOString(),
          endsAt: endsAt ? new Date(endsAt).toISOString() : null,
          notify,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["announcements"] });
      reset();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.detail);
        // A collision comes back with the absences that are in the way, so the
        // administrator can see what to move instead of guessing.
        const body = err.body as ConflictBody | undefined;
        setConflicts(body?.conflicts ?? []);
        return;
      }
      setError("Die Ansage konnte nicht angelegt werden.");
    },
  });

  function reset() {
    setUserId(0);
    setMessageId(0);
    setProxyId(0);
    setStartsAt("");
    setEndsAt("");
    setNotify("none");
    setError(null);
    setConflicts([]);
    preview.reset();
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setConflicts([]);
    create.mutate();
  }

  return (
    <Dialog
      open={open}
      title="Ansage planen"
      onClose={() => {
        reset();
        onClose();
      }}
    >
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <Field label="Benutzer">
          <Select
            required
            value={userId}
            onChange={(e) => {
              setUserId(Number(e.target.value));
              setMessageId(0);
            }}
          >
            <option value={0}>bitte wählen</option>
            {employees.map((u) => (
              <option key={u.id} value={u.id}>
                {u.lastName}, {u.firstName} — {u.email}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Ansage"
          hint={selected?.body}
        >
          <Select
            required
            disabled={!userId || messages.isLoading}
            value={messageId}
            onChange={(e) => setMessageId(Number(e.target.value))}
          >
            <option value={0}>
              {userId ? "bitte wählen" : "zuerst einen Benutzer wählen"}
            </option>
            {(messages.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Beginn">
            <Input
              type="datetime-local"
              required
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </Field>
          <Field
            label="Ende"
            hint={endRequired ? undefined : "leer lassen für „bis auf Weiteres“"}
          >
            <Input
              type="datetime-local"
              required={endRequired}
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </Field>
        </div>

        {selected?.proxyRequired && (
          <Field label="Vertretung">
            <Select
              required
              value={proxyId}
              onChange={(e) => setProxyId(Number(e.target.value))}
            >
              <option value={0}>bitte wählen</option>
              {employees
                .filter((u) => u.id !== userId)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.lastName}, {u.firstName}
                  </option>
                ))}
            </Select>
          </Field>
        )}

        {selected?.notifyAllowed && (
          <Field label="Benachrichtigung">
            <Select value={notify} onChange={(e) => setNotify(e.target.value)}>
              <option value="none">keine</option>
              <option value="self">an den Benutzer</option>
              <option value="self_and_proxy" disabled={!proxyId}>
                an Benutzer und Vertretung
              </option>
            </Select>
          </Field>
        )}

        {messageId > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => preview.mutate()}
                disabled={preview.isPending}
              >
                <Play className="size-4" aria-hidden />
                {preview.isPending ? "wird gerendert…" : "Vorschau anhören"}
              </Button>
              {preview.data && (
                <span className="text-xs muted">
                  {preview.data.durationSeconds.toFixed(1)} s
                </span>
              )}
            </div>

            {preview.error && (
              <ErrorNote>{(preview.error as Error).message}</ErrorNote>
            )}

            {preview.data && (
              <>
                <AudioPlayer src={preview.data.url} />
                {preview.data.missing.length > 0 && (
                  <ul className="text-xs text-amber-700 dark:text-amber-300">
                    {preview.data.missing.map((m, i) => (
                      <li key={i}>
                        Baustein {m.pattern}: {m.reason}
                      </li>
                    ))}
                  </ul>
                )}
                <ul className="text-xs muted">
                  {preview.data.segments.map((seg, i) => (
                    <li key={i}>
                      {i + 1}. {seg.resolved || seg.pattern}
                      {seg.reason ? ` (${seg.reason})` : ""}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {error && (
          <div className="flex flex-col gap-2">
            <ErrorNote>{error}</ErrorNote>
            {conflicts.length > 0 && (
              <ul className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--border)" }}>
                {conflicts.map((c) => (
                  <li key={c.id} className="muted">
                    {dateTime(c.startsAt)} – {endLabel(c.endsAt)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <Button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? "wird angelegt…" : "Planen"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
